import { Component, input, output } from '@angular/core';


export interface AlphabetLetter {
  letter: string;
  count: number;
  hasExperts: boolean;
}

@Component({
  standalone: true,
  imports: [],
  selector: 'lib-alphabet-selector',
  template: `
    <div class="rounded-xl p-4">
      <div class="flex flex-col gap-2">
        <!-- Clear Selection Button -->
        <button
          type="button"
          class="w-full flex items-center justify-between px-4 py-3 text-base rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation"
          [class]="
            selectedLetter() === null
              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25 transform scale-[0.98]'
              : 'text-gray-700 hover:bg-gray-300 active:bg-gray-100 active:scale-[0.98] bg-gray-200'
          "
          [disabled]="isLoading()"
          (click)="onLetterClick(null)"
        >
          <span class="font-semibold mr-1">All</span>
          <span
            class="px-2 py-1 rounded-lg text-sm font-medium"
            [class]="
              selectedLetter() === null
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 text-gray-600'
            "
          >
            {{ getTotalCount() }}
          </span>
        </button>

        <!-- Alphabet Letters Grid -->
        <div class="grid  gap-2 ">
          @for (letterObj of letters(); track letterObj.letter) { @if
          (letterObj.hasExperts) {
          <button
            type="button"
            class="flex items-center justify-between px-4 py-3 text-base rounded-xl transition-all duration-200 min-h-[48px] touch-manipulation"
            [class]="getLetterClasses(letterObj)"
            [disabled]="isLoading() || !letterObj.hasExperts"
            (click)="onLetterClick(letterObj.letter)"
          >
            <span class="font-semibold text-lg">{{ letterObj.letter }}</span>
            <span
              class="px-2 py-1 rounded-lg text-sm font-medium"
              [class]="getCountClasses(letterObj)"
            >
              {{ letterObj.count }}
            </span>
          </button>
          } }
        </div>
      </div>

      <!-- Loading State -->
      @if (isLoading()) {
      <div
        class="absolute inset-0 bg-white/50 rounded-xl flex items-center justify-center"
      >
        <div
          class="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"
        ></div>
      </div>
      }
    </div>
  `,
})
export class AlphabetSelectorComponent {
  letters = input.required<AlphabetLetter[]>();
  selectedLetter = input<string | null>(null);
  isLoading = input(false);

  letterSelect = output<string | null>();

  onLetterClick(letter: string | null) {
    if (this.isLoading()) return;
    this.letterSelect.emit(letter);
  }

  getLetterClasses(letterObj: AlphabetLetter): string {
    if (!letterObj.hasExperts) {
      return 'text-gray-300 cursor-not-allowed bg-gray-50';
    }

    if (this.selectedLetter() === letterObj.letter) {
      return 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25 transform scale-[0.98]';
    }

    return 'text-gray-700 bg-gray-200 hover:bg-gray-300 active:bg-gray-300 hover:shadow-md active:scale-[0.98]';
  }

  getCountClasses(letterObj: AlphabetLetter): string {
    if (this.selectedLetter() === letterObj.letter) {
      return 'bg-white/20 text-white';
    }
    return 'bg-white text-gray-600';
  }

  getTotalCount(): number {
    return this.letters().reduce(
      (total, letterObj) => total + letterObj.count,
      0
    );
  }

  getSelectedLetterCount(): number {
    const selectedLetterObj = this.letters().find(
      (l) => l.letter === this.selectedLetter()
    );
    return selectedLetterObj ? selectedLetterObj.count : 0;
  }
}
